import React, { useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form/form";
import { useForm } from "react-hook-form";
import { FancyInput, Input } from "../ui/input/input";
import { customerSchema } from "../../schemas/user-registration";
import { zodResolver } from "@hookform/resolvers/zod";
import { registerCustomer } from "../../api/customer.api";
import { toast } from "sonner";
import { ThreeDots } from "react-loader-spinner";
import { PasswordToggleWrapper } from "../ui/password-toggle-wrapper";
import { Checkbox } from "../../components/ui/input/Checkbox";
import ReactGA from "react-ga4";
import { useNavigate } from "react-router-dom";
import { StateList } from '../../utils/StatesList';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/input/select";

export default function AppointmentForm() {

  const stateDistrictMap = Object.fromEntries(
    StateList.map((item) => [item.state, item.districts]),
  );

  const navigate = useNavigate();
  const form = useForm({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      first_name: "",
      middle_name: "",
      last_name: "",
      phone_no: "",
      whatsapp_number: "",
      sameAsContact: false,
      email: "",
      gender: "",
      reffered_by: "",
      state: "",
      district: "",
      city: "",
      address: "",
      pin: "",
      password: "",
    },
  });
  const [isPending, setIsPending] = useState(false);

  const sameAsContact = form.watch("sameAsContact");
  const phoneNo = form.watch("phone_no");

  const selectedState = form.watch("state");
  const districts = stateDistrictMap[selectedState] || [];

  const formSubmit = async (values) => {
    setIsPending(true);

    try {
      const response = await registerCustomer(values);

      if (response.message.toLowerCase() === "success") {
        toast.custom(
          (t) => (
            <div className="p-4 bg-green-100 rounded-lg shadow-md border w-[360px]">
              <h4 className="text-green-600 text-center text-lg font-semibold">
                Registration Completed Successfully!
              </h4>
              <p className="text-gray-700 text-sm mt-2 text-center">
                Please check your email for instructions to access your account.
              </p>

              <div className="flex justify-center gap-3 mt-4">
                <button
                  className="px-3 py-1 rounded-lg bg-[#274760] text-white hover:bg-[#274760]/90 hover:shadow-md"
                  onClick={() => {
                    toast.dismiss(t.id);
                    navigate("/signup");
                  }}
                >
                  Login
                </button>

                <button
                  className="px-3 py-1 border-1 border-gray-300 rounded-lg"
                  onClick={() => toast.dismiss(t.id)}
                >
                  Hide
                </button>
              </div>
            </div>
          ),
          {
            duration: Infinity,
            closeButton: false,
          },
        );

        setIsPending(false);
        ReactGA.event("sign_up", {
          method: "email",
        });
      }
    } catch (error) {
      console.error(error);
      if (error.response && error.response.status === 403) {
        toast.custom(
          (t) => (
            <div className="p-4 bg-red-100 rounded-lg shadow-md border w-[360px]">
              <h4 className="text-red-600 text-center text-lg font-semibold">
                {error?.response?.data.message}
              </h4>

              <div className="flex justify-center gap-3 mt-4">
                <button
                  className="px-3 py-1 rounded-lg bg-[#274760] text-white hover:bg-[#274760]/90 hover:shadow-md"
                  onClick={() => {
                    toast.dismiss(t.id);
                    navigate("/signup");
                  }}
                >
                  Login
                </button>

                <button
                  className="px-3 py-1 border-1 border-gray-300 rounded-lg"
                  onClick={() => toast.dismiss(t.id)}
                >
                  Hide
                </button>
              </div>
            </div>
          ),
          {
            duration: Infinity,
            closeButton: false,
          },
        );
      } else {
        console.error(error);
        toast.error("Failed to register. Please try again later.");
      }
    } finally {
      setIsPending(false);
    }
  };

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(formSubmit)}>
          <div className="md:grid grid-cols-3 flex flex-col justify-between gap-4 md:gap-0">
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem classname="grow w-[35%]">
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="First Name"
                      {...field}
                      className="cs_form_field"
                      error={form.formState.errors.first_name}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="middle_name"
              render={({ field }) => (
                <FormItem classname="grow w-[33%]">
                  <FormLabel>Middle Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Middle Name"
                      {...field}
                      className="cs_form_field"
                      error={form.formState.errors.middle_name}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem classname="grow w-[30%]">
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Last Name"
                      {...field}
                      className="cs_form_field"
                      error={form.formState.errors.last_name}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div>
            <div className="md:grid grid-cols-2 gap-4 mb-2 hidden">
              <div className="md:col-start-2">
                <FormField
                  control={form.control}
                  name="sameAsContact"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 mt-3">
                      <FormControl>
                        <Checkbox
                          className="size-5 rounded-lg"
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked);
                            if (checked) {
                              form.setValue("whatsapp_number", phoneNo);
                            }
                          }}
                        />
                      </FormControl>
                      <FormLabel className="text-xs md:text-sm mt-0">
                        WhatsApp Number is same as Contact Number?
                      </FormLabel>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="md:grid grid-cols-2 flex flex-col gap-4 justify-between flex-wrap">
              <FormField
                control={form.control}
                name="phone_no"
                render={({ field }) => (
                  <FormItem className="grow">
                    <FormLabel>Contact Number</FormLabel>
                    <FormControl>
                      <Input
                        type="tel"
                        maxLength={10}
                        placeholder="Contact Number"
                        {...field}
                        onInput={(e) => {
                          e.target.value = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 10);
                          field.onChange(e);
                          if (sameAsContact) {
                            form.setValue(
                              "whatsapp_number",
                              e.currentTarget.value,
                            );
                          }
                        }}
                        className="cs_form_field"
                        error={form.formState.errors.phone_no}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="whatsapp_number"
                render={({ field }) => (
                  <FormItem className="grow">
                    <FormLabel>
                      Whatsapp Number
                      <div className="md:hidden">
                        <FormField
                          control={form.control}
                          name="sameAsContact"
                          render={({ field }) => (
                            <FormItem className="flex items-center gap-2 mt-3">
                              <FormControl>
                                <Checkbox
                                  className="size-5"
                                  checked={field.value}
                                  onCheckedChange={(checked) => {
                                    field.onChange(checked);
                                    if (checked) {
                                      form.setValue("whatsapp_number", phoneNo);
                                    }
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="text-xs md:text-sm">
                                Same as Contact Number?
                              </FormLabel>
                            </FormItem>
                          )}
                        />
                      </div>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="tel"
                        maxLength={10}
                        placeholder="Whatsapp Number"
                        {...field}
                        value={sameAsContact ? phoneNo : field.value}
                        onInput={(e) => {
                          e.target.value = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 10);
                          if (!sameAsContact) {
                            field.onChange(e);
                          }
                        }}
                        disabled={sameAsContact}
                        className="cs_form_field"
                        error={form.formState.errors.whatsapp_number}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="md:grid grid-cols-2 flex flex-col gap-4 justify-between my-4 flex-wrap">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="grow">
                  <FormLabel>
                    Email
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="example@gmail.com"
                      {...field}
                      type="email"
                      className="cs_form_field"
                      error={form.formState.errors.email}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Gender</FormLabel>

                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value);
                    }}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Gender" />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {["Male", "Female", "Others"].map((gender) => (
                        <SelectItem key={gender} value={gender}>
                          {gender}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="md:grid grid-cols-2 mt-4 gap-4 flex flex-col justify-between">
            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State</FormLabel>

                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value);

                      form.setValue("district", "");
                    }}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {StateList.map((item) => (
                        <SelectItem key={item.state} value={item.state}>
                          {item.state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="district"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>District</FormLabel>

                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={!selectedState}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            selectedState
                              ? "Select district"
                              : "Select state first"
                          }
                        />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {districts.map((district) => (
                        <SelectItem key={district} value={district}>
                          {district}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="grid grid-cols-2 mt-4 justify-between gap-4">
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem className="grow">
                  <FormLabel>City / Town</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="City / Town"
                      {...field}
                      className="cs_form_field"
                      error={form.formState.errors.city}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Address{" "}
                    <span className="text-[12px] text-gray-400">
                      ( Short Address )
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter your short address"
                      className="cs_form_field"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="pin"
              render={({ field }) => (
                <FormItem className="grow">
                  <FormLabel>Pin Code</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Pin Code"
                      maxLength={6}
                      {...field}
                      className="cs_form_field"
                      error={form.formState.errors.pin}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="md:grid grid-cols-2 my-4 gap-4">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <PasswordToggleWrapper>
                      <FancyInput
                        label="Password"
                        error={form.formState.errors.password}
                        {...field}
                      />
                    </PasswordToggleWrapper>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {form.formState.errors.password && (
              <div className="text-sm text-gray-400">
                <p className="m-0">
                  Password must contain at least 1 uppercase letters, 1
                  lowercase letters, 1 numbers, 1 special characters(@$!%*?&)
                </p>
              </div>
            )}
            <FormField
              control={form.control}
              name="reffered_by"
              render={({ field }) => (
                <FormItem className="grow">
                  <FormLabel>Referred By</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Employee Code"
                      {...field}
                      className="cs_form_field"
                      error={form.formState.errors.pin}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-center mt-12">
            {isPending ? (
              <div className="flex gap-3 items-center border-2 border-[#274760] rounded-full px-4">
                <span className="text-lg text-[#274760]">Registering</span>
                <ThreeDots
                  visible={true}
                  height="40"
                  width="40"
                  color="#274760"
                  radius="9"
                  ariaLabel="three-dots-loading"
                  wrapperStyle={{}}
                  wrapperClass=""
                />
              </div>
            ) : (
              <button
                className="cs_btn cs_style_1"
                type="submit"
                disabled={isPending}
              >
                <span>Register</span>
                <i>
                  <img src="/images/icons/arrow_white.svg" alt="Icon" />
                  <img src="/images/icons/arrow_white.svg" alt="Icon" />
                </i>
              </button>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}
